// src/lib/services/data/MessageRouter.ts
// Validates and routes WebSocket messages to DataSyncService.
// Called from websocket.actions.svelte.ts handler functions.

import { dataSyncService } from './DataSyncService';
import { parseSequence } from './deltaOrdering';
import { createLogger } from '$lib/utils/logger';
import { setLayerStatus } from '$lib/state/health.state.svelte';
import type {
	DataDeltaMessage,
	DataDeltaBatchMessage,
	DataSnapshotMessage
} from '$lib/types/websocket/messages.types';

const log = createLogger('MessageRouter');

/**
 * Build a delta item that carries the change's id under both keys the reducers
 * match on — alerts on `alert_flag_id`, work_orders/hardware on `id`.
 *
 * The backend's DataDeltaEvent serializes `data` as `{}` by default (never
 * null) and a remove carries the affected id only at the TOP level (e.g.
 * `{action:'remove', id, data:{}}`, or a delete with `data:{wo_number}` that
 * lacks an id). So `data ?? {id}` is not enough — an empty/partial data object
 * would pass through without the id, and the reducer silently drops the delta.
 * We fill the id under both keys only when absent, never overwriting the real
 * values present on add/update payloads.
 */
function withDeltaId(
	data: Record<string, unknown> | undefined,
	id: string | undefined
): Record<string, unknown> {
	const item: Record<string, unknown> = { ...(data ?? {}) };
	if (id != null) {
		if (item.id === undefined) item.id = id;
		if (item.alert_flag_id === undefined) item.alert_flag_id = id;
	}
	return item;
}

// ---------------------------------------------------------------------------
// routeDataSnapshot
// ---------------------------------------------------------------------------

/**
 * Route a DATA_SNAPSHOT message to DataSyncService.
 *
 * Extracts `entity` and `data` from the message. When entity is absent the
 * backend is sending a combined "all" snapshot (alerts + work orders in one).
 *
 * The `data_status` field now arrives intact thanks to the Phase 5 schema
 * fix — previously Zod's `.parse()` was stripping it because it wasn't
 * declared on DataSnapshotMessageSchema. MessageRouter then always saw
 * `undefined` and fell through to `'fresh'`, which defeated the entire
 * Phase 4 empty+degraded guard.
 *
 * Forwards the raw message so DataSyncService can also extract
 * `snapshot_ts` from wherever the backend chose to put it (top level,
 * metadata, or inside data).
 */
export function routeDataSnapshot(message: DataSnapshotMessage): void {
	const entity = message.entity || 'all';
	const data = message.data;

	// P7: structural guard. A missing data field is NOT a valid snapshot
	// and must not silently pass through. Flip the API layer to degraded
	// so the badge shows something is wrong, and drop the message.
	if (!data) {
		log.warn('routeDataSnapshot: no data in message — dropping', { entity });
		setLayerStatus('api', 'degraded', `DATA_SNAPSHOT missing data field (entity=${entity})`);
		return;
	}

	// P11: do NOT default missing data_status to 'fresh'. The whole point
	// of P4/P5 was to stop treating absence-of-information as success. A
	// message without data_status is either from a legacy backend build
	// or a code path that forgot to thread it through — both of which
	// indicate uncertainty, not freshness. Route to 'unknown' which the
	// reducer treats as non-fresh and therefore DEGRADED.
	const dataStatus = (message as { data_status?: string }).data_status || 'unknown';

	dataSyncService.handleSnapshot(
		entity,
		data as unknown as Parameters<typeof dataSyncService.handleSnapshot>[1],
		dataStatus as Parameters<typeof dataSyncService.handleSnapshot>[2],
		'live',
		message as unknown as Record<string, unknown>
	);
}

// ---------------------------------------------------------------------------
// routeDataDelta
// ---------------------------------------------------------------------------

/**
 * Route a DATA_DELTA message to DataSyncService.
 *
 * Supports bulk remove when `ids` is provided.
 */
export function routeDataDelta(message: DataDeltaMessage): void {
	const { entity, action, data, id, ids } = message;

	if (!entity || !action) {
		log.warn('routeDataDelta: missing entity or action', { entity, action });
		return;
	}

	// Live WS message: carry the envelope sequence (for per-entity stale
	// rejection) and isLive (so it advances the live epoch for REST gating).
	const ctx = { seq: parseSequence((message as { sequence?: unknown }).sequence), isLive: true };

	// Bulk remove: iterate each id. For alert entities the id IS the
	// alert_flag_id the reducer matches on; for work_orders the reducer matches
	// on id. Provide both keys so the synthesized remove item matches either
	// reducer regardless of entity. All ids share the envelope sequence.
	if (action === 'remove' && ids && ids.length > 0) {
		for (const removeId of ids) {
			dataSyncService.handleDelta(entity, action, { id: removeId, alert_flag_id: removeId }, ctx);
		}
		return;
	}

	// Single-item delta
	dataSyncService.handleDelta(
		entity,
		action,
		withDeltaId(data as Record<string, unknown> | undefined, id),
		ctx
	);
}

// ---------------------------------------------------------------------------
// routeDataDeltaBatch
// ---------------------------------------------------------------------------

/**
 * Route a DATA_DELTA_BATCH message to DataSyncService.
 *
 * P9: alert deltas are grouped and sent through the batch path
 * (handleAlertsDeltaBatch) which processes all K deltas with a single
 * grouped-array rebuild. Previously each alert delta called
 * applyAlertDelta individually which rebuilt both grouped arrays K
 * times, giving O(batch × n) behavior on the /alerts page during
 * bursty updates. Non-alert entities still go per-item because their
 * update cost is already linear in the delta count.
 */
export function routeDataDeltaBatch(message: DataDeltaBatchMessage): void {
	const { items } = message;

	if (!items || items.length === 0) {
		log.warn('routeDataDeltaBatch: no items in message');
		return;
	}

	// Group for logging purposes
	const groupCounts = new Map<string, number>();

	// Split into alert batch vs everything else.
	// Alerts entity includes both 'alerts' and 'approved_alerts' — they
	// share the same underlying state and the batch function routes them.
	const alertBatch: Array<{ action: string; data: Record<string, unknown> }> = [];
	const nonAlertItems: typeof items = [];

	for (const item of items) {
		const { entity, action, data, id } = item;

		if (!entity || !action) {
			log.warn('routeDataDeltaBatch: item missing entity or action', { entity, action });
			continue;
		}

		const key = `${entity}:${action}`;
		groupCounts.set(key, (groupCounts.get(key) ?? 0) + 1);

		if (entity === 'alerts' || entity === 'approved_alerts') {
			// Thread the id under both keys (data defaults to {} on the backend) so
			// a remove item is matched by applyAlertDeltaBatch.
			alertBatch.push({
				action,
				data: withDeltaId(data as Record<string, unknown> | undefined, id)
			});
		} else {
			nonAlertItems.push(item);
		}
	}

	// All items in a DATA_DELTA_BATCH share the envelope's sequence.
	const ctx = { seq: parseSequence((message as { sequence?: unknown }).sequence), isLive: true };

	// Alert batch: single O(N + K) pass (coalesced + stale-filtered inside)
	if (alertBatch.length > 0) {
		dataSyncService.handleAlertsDeltaBatch(alertBatch, ctx);
	}

	// Everything else: per-item (work orders, hardware alerts, etc.)
	for (const item of nonAlertItems) {
		const { entity, action, data, id } = item;
		if (!entity || !action) continue;
		dataSyncService.handleDelta(
			entity,
			action,
			withDeltaId(data as Record<string, unknown> | undefined, id),
			ctx
		);
	}

	log.debug('routeDataDeltaBatch processed', {
		totalItems: items.length,
		groups: Object.fromEntries(groupCounts)
	});
}

// ---------------------------------------------------------------------------
// routeDataTick
// ---------------------------------------------------------------------------

/**
 * Route a DATA_TICK message to DataSyncService.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function routeDataTick(message: any): void {
	// The tick message carries alerts_diff/work_orders_diff at the top level
	// plus the envelope sequence. All diff items share that one sequence.
	const ctx = { seq: parseSequence(message?.sequence), isLive: true };
	dataSyncService.handleTick(message, ctx);
}
